import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

// Portal route → page title mapping
const PORTAL_TITLES = {
  '': 'Overview',
  'invoices': 'Invoices',
  'estimates': 'Estimates',
  'pay': 'Make Payment',
  'payment-history': 'Payment History',
  'payment-result': 'Payment Result',
};

function PortalLayout({ portalData, children }) {
  const location = useLocation();
  const { token, publicId, shortCode } = useParams();

  // Use the portalData that's already been fetched
  const customerInfo = portalData ? {
    customer: {
      publicId: portalData.customerPublicId,
      name: portalData.customerName,
      email: portalData.customerEmail || '',
      merchantName: portalData.merchantName
    },
    summary: {},
    industryType: portalData.industryType
  } : null;

  // Build the base path for navigation
  const basePath = shortCode ? `/p/${shortCode}` : token ? `/t/${token}` : `/portal/${publicId}`;
  const merchantName = portalData?.merchantName || 'Customer Portal';

  // Per-page document title — format: "PageTitle | MerchantName"
  useEffect(() => {
    const relativePath = location.pathname.replace(basePath, '').replace(/^\//, '');
    const invoiceMatch = relativePath.match(/^invoices\/(.+)$/);
    const estimateMatch = relativePath.match(/^estimates\/(.+)$/);
    let pageTitle;
    if (invoiceMatch) {
      pageTitle = `Invoice ${invoiceMatch[1]}`;
    } else if (estimateMatch) {
      pageTitle = `Estimate ${estimateMatch[1]}`;
    } else {
      pageTitle = PORTAL_TITLES[relativePath] || 'Customer Portal';
    }
    document.title = `${pageTitle} | ${merchantName}`;
  }, [location.pathname, basePath, merchantName]);

  const isActive = (path) => {
    const currentPath = location.pathname.replace(basePath, '').replace(/^\//, '');
    
    // For Overview (empty path)
    if (path === '') {
      return currentPath === '' || currentPath === '/';
    }
    
    // For other paths, check if current path starts with the link path
    // This handles both exact matches and sub-routes
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  if (!customerInfo) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {customerInfo.customer.merchantName}
              </h1>
              <p className="text-sm text-gray-500">Customer Portal</p>
            </div>
            <div className="text-right">
              <p className="font-medium">{customerInfo.customer.name}</p>
              <p className="text-sm text-gray-500">{customerInfo.customer.email}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <Link
              to={basePath}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                isActive('') 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </Link>
            <Link
              to={`${basePath}/invoices`}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                isActive('invoices') 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoices
            </Link>
            <Link
              to={`${basePath}/payment-history`}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                isActive('payment-history') 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payment History
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}

export default PortalLayout;
